import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Picture from '../Picture';
import AnalysisOutput from './AnalysisOutput';
import { API_BASE_URL } from '../../config.js';
import { buttonClass, formatCost, ghostButtonClass, inputClass } from '../../pages/picture-ai/usePictureAi';

/**
 * Try a configuration on one real picture, end to end: find it, look at it, run it.
 *
 * Seeing the picture before spending an analysis on it is the point — an id alone gives no way to
 * tell whether it is the kind of photo the configuration is meant for. The run goes through the
 * exact same reservation, provider and normalization path as the worker; only the scheduling
 * differs.
 *
 * Which is why the model is named up front: the test runs the *saved* version, so comparing two
 * models means saving between the two runs. Announcing the model that is about to run is what
 * keeps a comparison from silently measuring the same one twice.
 */
const TestPictureModal = ({ open, onClose, configuration, agent, params, models, pendingModel, fetchWithAuth, request }) => {
  const { t } = useTranslation();
  const [pictureId, setPictureId] = useState('');
  const [picture, setPicture] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const labelOf = model => (models || []).find(entry => entry.model === model)?.label || model;
  const runningModel = configuration?.model ? labelOf(configuration.model) : null;
  const staleModel = pendingModel && configuration?.model && pendingModel !== configuration.model;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Looking the picture up as the id is typed, so the preview follows without a second button.
  useEffect(() => {
    let cancelled = false;
    const id = pictureId.trim();
    setPicture(null);
    setResult(null);
    setError('');
    if (!id || !/^\d+$/.test(id)) return undefined;

    const timeout = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/pictures/${id}`);
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || t('Picture not found'));
        const covered = (configuration?.programs || []).some(program => program.projectkey === payload.projectkey);
        if (!covered) {
          throw new Error(t('This picture belongs to {{projectkey}}, which this configuration does not cover.', { projectkey: payload.projectkey }));
        }
        if (!cancelled) setPicture(payload);
      } catch (caught) {
        if (!cancelled) setError(caught.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => { cancelled = true; window.clearTimeout(timeout); };
  }, [pictureId, configuration, fetchWithAuth, t]);

  const runAnalysis = async () => {
    setRunning(true);
    setError('');
    try {
      setResult(await request(`/admin/configurations/${configuration.id}/pictures/${picture.id}/test`, {
        method: 'POST',
        body: JSON.stringify({ config_version_id: configuration.active_version_id })
      }));
    } catch (caught) {
      setError(caught.message);
    } finally {
      setRunning(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className="mt-8 max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900"
        onClick={event => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('Test on one picture')}</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('Runs the active version on a real picture, through the same path as the worker, without publishing anything: the picture keeps its current analysis and its visibility.')}
              {runningModel && ` ${t('Model: {{model}}.', { model: runningModel })}`}
            </p>
          </div>
          <button className="rounded-lg px-3 py-1 text-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" onClick={onClose}>×</button>
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{t('Picture id')}</span>
            <input
              autoFocus className={`${inputClass} w-64`} value={pictureId}
              placeholder="226859109"
              onChange={event => setPictureId(event.target.value)}
            />
          </label>
          {loading && <span className="pb-2 text-sm text-gray-500">{t('Loading…')}</span>}
          {picture && !result && (
            <button className={buttonClass} disabled={running} onClick={runAnalysis}>
              {running ? t('Analyzing…') : t('Run the analysis')}
            </button>
          )}
          {result && (
            <button className={ghostButtonClass} onClick={() => { setResult(null); setPictureId(''); }}>
              {t('Test another picture')}
            </button>
          )}
        </div>

        {staleModel && (
          <p className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            {t('The editor is set to {{selected}}, but that is not saved yet: this test runs {{saved}}. Save first to try the other one.', {
              selected: labelOf(pendingModel), saved: runningModel
            })}
          </p>
        )}

        {error && (
          <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">{error}</p>
        )}

        {!picture && !error && !loading && (
          <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
            {t('Enter the id of a picture from one of this configuration’s programs to preview it.')}
          </p>
        )}

        {picture && (
          <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
            <div className="lg:sticky lg:top-0 lg:self-start">
              <div className="overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-950">
                <Picture pictureurl={picture.pictureurl} lazy className="h-[min(56vh,38rem)] w-full object-contain" />
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-sm text-gray-700 dark:text-gray-300">
                {[
                  [t('Program'), picture.projectkey],
                  [t('Question'), picture.translatedquestion || picture.question],
                  [t('Declared item'), picture.item],
                  [t('Store'), picture.shopname],
                  [t('City'), picture.shopcity],
                  [t('Taken on'), picture.createdat ? `${picture.createdat}`.slice(0, 10) : null],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs uppercase text-gray-500">{label}</dt>
                    <dd>{value || '—'}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div>
              {!result && (
                <p className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  {t('Check that this is the kind of picture you expect, then run the analysis.')}
                </p>
              )}

              {result && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {result.status} · {result.model_used || result.configured_model} · {formatCost(result.estimated_cost_micros)}
                    {result.input_tokens != null && ` · ${t('{{input}} in / {{output}} out tokens', { input: result.input_tokens, output: result.output_tokens })}`}
                    {result.publication_skipped_reason && result.publication_skipped_reason !== 'publication_not_requested'
                      && ` · ${t('not published: {{reason}}', { reason: result.publication_skipped_reason })}`}
                  </p>

                  {result.normalized_output
                    ? <AnalysisOutput output={result.normalized_output} agent={agent} configParams={params} />
                    : (
                      <p className="rounded-xl bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">
                        {result.error?.message || t('The analysis produced no result.')}
                      </p>
                    )}

                  <details>
                    <summary className="cursor-pointer text-sm text-gray-500">{t('Raw output and diagnostics')}</summary>
                    <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-xl bg-gray-950 p-4 text-xs text-gray-100">
                      {result.raw_output || ''}{result.error ? `\n\n${JSON.stringify(result.error, null, 2)}` : ''}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestPictureModal;
